import { useCallback, useState } from "react";
import { IconArrowRight, IconBook, IconClose, IconFolderAdd, IconSparks, IconUserAdd } from "@humansignal/icons";
import { useTranslation } from "../../i18n/LocaleProvider";
import { spaNavigate } from "../../utils/navigation";

// First-run onboarding nudge. A friendly "what do you want to do?" router
// so a brand-new user isn't dropped onto a blank dashboard. Dismissible and
// gated by what the user can actually do — a plain annotator never sees the
// "create a project" choice. Lives above the hero on the Home page.

// localStorage flag: once dismissed (either the ✕ or a choice that routes
// the user onward), the nudge stays gone across sessions. Versioned so a
// future revamp can re-introduce it without stale keys blocking it.
const DISMISS_KEY = "afri:onboarding-dismissed:v1";

export const isOnboardingDismissed = (): boolean => {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
};

const persistDismiss = () => {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* private-mode / storage-disabled — the nudge simply reappears */
  }
};

type Props = {
  /** OW/AD/MA/staff — anyone who can create projects in this org. */
  canCreate: boolean;
  /** Resolved docs URL for the self-hosting / API path. */
  docsUrl: string;
  /** Opens the create-project modal (owned by the parent Home page). */
  onCreate: () => void;
};

export function OnboardingCard({ canCreate, docsUrl, onCreate }: Props) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(isOnboardingDismissed);

  const dismiss = useCallback(() => {
    persistDismiss();
    setDismissed(true);
  }, []);

  if (dismissed) return null;

  return (
    <section
      className="lsf-home-page__onboard"
      aria-label={t("pages.home.onboarding.aria", undefined, "Getting started")}
    >
      <div className="lsf-home-page__onboard-head">
        <div>
          <h2 className="lsf-home-page__onboard-title">
            {t("pages.home.onboarding.title", undefined, "What would you like to do?")}
          </h2>
          <p className="lsf-home-page__onboard-sub">
            {t("pages.home.onboarding.sub", undefined, "Pick a starting point, you can always change direction later.")}
          </p>
        </div>
        <button
          type="button"
          className="lsf-home-page__onboard-close"
          onClick={dismiss}
          aria-label={t("pages.home.onboarding.dismiss", undefined, "Don't show this again")}
          title={t("pages.home.onboarding.dismiss", undefined, "Don't show this again")}
        >
          <IconClose />
        </button>
      </div>

      <div className="lsf-home-page__onboard-grid">
        {/* Create — only for owners/managers who actually can. */}
        {canCreate && (
          <button
            type="button"
            className="lsf-home-page__onboard-choice"
            onClick={() => {
              dismiss();
              onCreate();
            }}
          >
            <span className="lsf-home-page__onboard-icon" aria-hidden>
              <IconFolderAdd />
            </span>
            <span className="lsf-home-page__onboard-copy">
              <span className="lsf-home-page__onboard-choice-title">
                {t("pages.home.onboarding.create_title", undefined, "Create your first project")}
              </span>
              <span className="lsf-home-page__onboard-choice-desc">
                {t(
                  "pages.home.onboarding.create_desc",
                  undefined,
                  "Set up a labeling task, add your data, and invite your team.",
                )}
              </span>
            </span>
            <IconArrowRight className="lsf-home-page__onboard-go" aria-hidden />
          </button>
        )}

        {/* Join / start labeling — everyone. */}
        <button
          type="button"
          className="lsf-home-page__onboard-choice"
          onClick={() => {
            dismiss();
            spaNavigate("/discover");
          }}
        >
          <span className="lsf-home-page__onboard-icon" aria-hidden>
            <IconUserAdd />
          </span>
          <span className="lsf-home-page__onboard-copy">
            <span className="lsf-home-page__onboard-choice-title">
              {t("pages.home.onboarding.join_title", undefined, "Join a project & start labeling")}
            </span>
            <span className="lsf-home-page__onboard-choice-desc">
              {t(
                "pages.home.onboarding.join_desc",
                undefined,
                "Browse open projects on Discover and contribute annotations.",
              )}
            </span>
          </span>
          <IconArrowRight className="lsf-home-page__onboard-go" aria-hidden />
        </button>

        {/* Developer path — external docs. Only when we have a docs URL. */}
        {docsUrl && (
          <a
            className="lsf-home-page__onboard-choice"
            href={docsUrl}
            target="_blank"
            rel="noreferrer"
            onClick={dismiss}
          >
            <span className="lsf-home-page__onboard-icon" aria-hidden>
              <IconBook />
            </span>
            <span className="lsf-home-page__onboard-copy">
              <span className="lsf-home-page__onboard-choice-title">
                {t("pages.home.onboarding.docs_title", undefined, "Run it yourself")}
              </span>
              <span className="lsf-home-page__onboard-choice-desc">
                {t(
                  "pages.home.onboarding.docs_desc",
                  undefined,
                  "API, self-hosting and integration guides in the developer docs.",
                )}
              </span>
            </span>
            <IconSparks className="lsf-home-page__onboard-go" aria-hidden />
          </a>
        )}
      </div>
    </section>
  );
}
